import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { usePortalAuth } from '../../context/PortalAuthContext';

function CustomerPortalSetup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, isAuthenticated } = usePortalAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const token = searchParams.get('token');

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/portal');
    }
  }, [isAuthenticated, navigate]);

  // Show error if no token
  if (!token) {
    return (
      <div className="portal-auth-page">
        <div className="portal-auth-container">
          <div className="portal-auth-header">
            <h1>Invalid Link</h1>
            <p>This invitation link is invalid or has expired.</p>
          </div>
          <Link to="/portal/login" className="portal-btn-primary" style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
            Go to Login
          </Link>
        </div>
        <style>{portalStyles}</style>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    const result = await register(token, password);

    if (result.success) {
      navigate('/portal');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="portal-auth-page">
      <div className="portal-auth-container">
        <div className="portal-auth-header">
          <h1>Set Up Your Account</h1>
          <p>Create a password to access your customer portal</p>
        </div>

        {error && (
          <div className="portal-message error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="portal-auth-form">
          <div className="form-group">
            <label htmlFor="password">Create Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              placeholder="At least 8 characters"
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              placeholder="Re-enter your password"
            />
          </div>

          <button type="submit" className="portal-btn-primary" disabled={loading}>
            {loading ? 'Setting up...' : 'Complete Setup'}
          </button>
        </form>

        <div className="portal-auth-links">
          <Link to="/portal/login">Already have an account? Sign in</Link>
        </div>
      </div>
      <style>{portalStyles}</style>
    </div>
  );
}

const portalStyles = `
  .portal-auth-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
    padding: 20px;
  }
  .portal-auth-container {
    background: white;
    border-radius: 16px;
    padding: 40px;
    width: 100%;
    max-width: 400px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  }
  .portal-auth-header {
    text-align: center;
    margin-bottom: 32px;
  }
  .portal-auth-header h1 {
    font-size: 1.75rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 8px 0;
  }
  .portal-auth-header p {
    color: #64748b;
    margin: 0;
    font-size: 0.95rem;
  }
  .portal-message {
    padding: 12px 16px;
    border-radius: 8px;
    margin-bottom: 24px;
    font-size: 0.9rem;
  }
  .portal-message.error {
    background: #fef2f2;
    color: #dc2626;
    border: 1px solid #fecaca;
  }
  .portal-auth-form .form-group {
    margin-bottom: 20px;
  }
  .portal-auth-form label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 6px;
  }
  .portal-auth-form input {
    width: 100%;
    padding: 12px 14px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 1rem;
    transition: border-color 0.15s, box-shadow 0.15s;
    box-sizing: border-box;
  }
  .portal-auth-form input:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }
  .portal-btn-primary {
    width: 100%;
    padding: 14px 20px;
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
  }
  .portal-btn-primary:hover:not(:disabled) {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    transform: translateY(-1px);
  }
  .portal-btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .portal-auth-links {
    text-align: center;
    margin-top: 24px;
  }
  .portal-auth-links a {
    color: #3b82f6;
    text-decoration: none;
    font-size: 0.9rem;
  }
  .portal-auth-links a:hover {
    text-decoration: underline;
  }
`;

export default CustomerPortalSetup;
